/**
 * Relative timestamps without a date library.
 *
 * The project has no date-fns/dayjs/luxon, and adding one for this would be
 * disproportionate. Intl.RelativeTimeFormat is built in and handles the
 * pluralization and wording for us.
 */

const DIVISIONS = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** @returns {string} e.g. "3 minutes ago", "in 2 hours" */
export function formatRelative(value) {
  if (!value) return '—';
  const ms = typeof value === 'number' ? value : Date.parse(value);
  if (Number.isNaN(ms)) return '—';

  let duration = (ms - Date.now()) / 1000;
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return '—';
}

/** @returns {string} compact countdown, e.g. "1h 43m". Empty once elapsed. */
export function formatCountdown(target) {
  if (!target) return '';
  const ms = Date.parse(target) - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return '';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.max(1, Math.floor(ms / 1000))}s`;
}

export function formatAbsolute(value) {
  if (!value) return '—';
  const ms = typeof value === 'number' ? value : Date.parse(value);
  if (Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function RelativeTime({ value, className = '' }) {
  if (!value) return <span className={className}>—</span>;
  return (
    <time dateTime={typeof value === 'string' ? value : undefined} title={formatAbsolute(value)} className={className}>
      {formatRelative(value)}
    </time>
  );
}
